// Generated from src/cardRecord.ts. scripts/test-packages.mjs checks parity.
const text = (v) => (typeof v === "string" ? v.trim() : "");
const count = (v) => typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : null;
export function cardEditionLabel(ordinal, cap) {
    const n = count(ordinal), limit = count(cap);
    if (n && (!limit || n <= limit))
        return `No. ${String(n).padStart(3, "0")}${limit ? ` / ${limit}` : ""}`;
    return limit ? `Edition of ${limit}` : null;
}
export function cardRecordState(card, demo = false) {
    if (demo)
        return "Preview · not collected";
    if (card.ownership_status === "revoked" || card.status === "revoked")
        return "Collection revoked";
    const states = {
        pending_offline: "Saved for sync",
        offline_pending: "Waiting for verification",
        pending_review: "Confirmation pending",
        pending: "Confirmation pending",
        rejected: "Not collected",
        error: "Not recorded",
        verifying: "Verifying",
        duplicate: "Already in Passport",
        owner_self: "Your own record",
    };
    if (states[card.status || ""])
        return states[card.status];
    // A soft risk flag does not change an authoritative added result into pending.
    if (card.riskFlagged)
        return "Collection recorded";
    if (card.evidence === "offline_pending")
        return "Waiting for verification";
    if (!card.earned_at && card.status !== "added")
        return "Ready to discover";
    const evidence = {
        staff: "Confirmed by staff",
        staff_verified: "Confirmed by staff",
        location: "Location checked",
        proximity_verified: "Location checked",
        event_verified: "Event verified",
        basic_qr: "QR collected",
        qr: "QR collected",
    };
    return evidence[card.evidence || ""] || "Collection recorded";
}
/** No timezone conversion of server-local dates; never relabel a collection window as an event date. */
function recordDate(value) {
    const date = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/);
    if (!date)
        return "";
    const [, year, month, day] = date;
    const parsed = new Date(`${year}-${month}-${day}T12:00:00Z`);
    return Number.isNaN(parsed.valueOf()) ||
        parsed.toISOString().slice(0, 10) !== `${year}-${month}-${day}`
        ? ""
        : `${month}/${day}/${year}`;
}
export function makeCardRecord(card, demo = false) {
    const kind = card.subject_type ||
        (card.design?.template === "build" ? "vehicle" : card.design?.template) ||
        "card";
    const labels = {
        vehicle: "Build specification",
        shop: "Shop record",
        club: "Club record",
        event: "Event record",
        campaign: "Discovery record",
        booth: "Booth record",
        activation: "Activation record",
        destination: "Destination record",
        checkpoint: "Checkpoint record",
        place: "Place record",
        commemorative: "Commemorative provenance",
    };
    const subject = card.subject;
    const meta = subject?.collectible;
    const subtitle = text(subject?.subtitle) || text(meta?.subtitle);
    const campaign = text(meta?.campaign_title);
    const issuer = text(card.issuer) || "Bilt:Spec";
    const state = cardRecordState(card, demo);
    const method = { staff: "Staff confirmation", location: "Location check", qr: "QR code" }[card.rules?.evidence || ""] || "-";
    const location = text(card.rules?.venue_name);
    const kindLabel = kind === "vehicle" ? "Build" : kind.charAt(0).toUpperCase() + kind.slice(1);
    let feature = {
        label: "Discovery",
        value: campaign || kindLabel,
        note: subtitle || "Passport record",
    };
    if (kind === "shop")
        feature = {
            label: "Location",
            value: subtitle || location || "-",
            note: "Public shop record",
        };
    if (kind === "club")
        feature = {
            label: "Community",
            value: subtitle || "-",
            note: "Public club record · not a membership credential",
        };
    if (kind === "event")
        feature = {
            label: "Event details",
            value: subtitle || "-",
            note: "Public event record · not an admission ticket",
        };
    if (kind === "vehicle") {
        const power = Number(subject?.facts?.power);
        const unit = text(subject?.facts?.power_unit);
        const known = Number.isFinite(power) && power > 0 && ["HP", "WHP"].includes(unit);
        feature = {
            label: "Power",
            value: known ? `${power.toLocaleString("en-US")} ${unit}` : "-",
            note: known
                ? `Build-reported · ${unit === "WHP" ? "at the wheels" : subject?.facts?.power_measurement === "crank" ? "at the crank" : "measurement not specified"}`
                : "No estimated horsepower",
        };
    }
    // A missing subject is an intentional privacy boundary, not a loading placeholder.
    if (subject === null)
        feature = {
            label: "Collection record",
            value: "Private subject",
            note: "Subject details are no longer public",
        };
    const facts = [
        { label: "Card type", value: kindLabel },
        { label: "Collection method", value: method },
    ];
    if (kind === "vehicle" && subject) {
        const mods = count(subject.modification_count ?? meta?.modification_count);
        facts[0] = {
            label: "Documented mods",
            value: mods === null ? "-" : String(mods),
        };
        const owner = text(subject.owner_username) || text(meta?.username);
        if (owner)
            facts.push({ label: "Builder", value: `@${owner}` });
    }
    const earned = recordDate(card.earned_at);
    if (earned && !demo)
        facts.push({ label: "Recorded", value: earned });
    const starts = recordDate(card.rules?.starts_at);
    if (starts)
        facts.push({ label: "Collection opens", value: starts });
    const ends = recordDate(card.rules?.ends_at);
    if (ends)
        facts.push({ label: "Collection closes", value: ends });
    if (location && kind !== "shop")
        facts.push({ label: "Collection venue", value: location });
    return {
        kind,
        label: labels[kind] || "Passport record",
        title: card.title,
        subtitle,
        feature,
        facts,
        story: text(card.design?.story),
        issuer,
        state,
        edition: cardEditionLabel(card.ordinal, card.rules?.cap),
    };
}
